import { isRagIngestedProductSource } from '@/features/product-catalog/utils/isRagIngestedProductSource'
import { isProductSourceIngestionInFlight } from '@/features/product-catalog/utils/productSourceUploadStatus'
import { apiClient } from '@/shared/api/apiClient'
import { PRODUCTS_QUERY_KEY } from '@/shared/hooks/api/product'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type IngestionProgress = {
  percent: number
  current?: number
  total?: number
}

export type FileUploadStatus = 'PENDING' | 'UPLOADED' | 'FAILED'

export type ProductSource = {
  id: string
  productId: string
  type: string
  name: string
  filePathInBucket: string
  fileSizeKb: number | null
  fileUploadStatus: FileUploadStatus
  content: string | null
  createdAt: Date
  updatedAt: Date | null
  deletedAt: Date | null
  ingestionStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  ingestionStatusUpdatedAt: Date | null
  ingestionError: string | null
  ingestionErrorCode: string | null
  ingestionStage: string | null
  ingestionProgress: IngestionProgress | null
  ingestionEmbeddingTokens: number | null
  ingestionEmbeddingModel: string | null
  ingestionPartialResult: boolean | null
  ingestionMissingPages: string[]
  ragJobId: string | null
  summary: string | null
  classification: string | null
  classificationPurposeOrUtility: string | null
  ragDocumentId: string | null
  initialBatchId: string | null
}

export const PRODUCT_SOURCES_KEY = 'product-sources' as const

const CLASSIFICATION_POLL_WINDOW_MS = 2 * 60 * 1000

function shouldPollForClassification(source: ProductSource): boolean {
  if (!isRagIngestedProductSource(source)) {
    return false
  }
  if (source.classification !== null) {
    return false
  }
  if (!source.ingestionStatusUpdatedAt) {
    return false
  }
  const elapsedMs = Date.now() - source.ingestionStatusUpdatedAt.getTime()
  return elapsedMs <= CLASSIFICATION_POLL_WINDOW_MS
}
export const PRODUCT_SOURCE_CHUNKS_KEY = 'product-source-chunks' as const
export const PRODUCT_SOURCE_DOWNLOAD_URL_KEY =
  'product-source-download-url' as const

export type ProductSourceChunk = {
  index: number
  page: number
  section: string
  rawText: string
}

export type ProductSourceChunksResponse = {
  ragDocumentId: string
  chunks: ProductSourceChunk[]
}

function parseOptionalNumber(value: unknown): number | null {
  if (value == null) {
    return null
  }
  if (typeof value === 'number') {
    return value
  }
  if (typeof value === 'string') {
    const parsedValue = Number(value)
    if (!Number.isNaN(parsedValue)) {
      return parsedValue
    }
  }
  return null
}

function parseOptionalString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value
  }
  return null
}

function parseOptionalBoolean(value: unknown): boolean | null {
  if (value === true || value === false) {
    return value
  }
  return null
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.map((item) => String(item))
}

function parseFileUploadStatus(value: unknown): FileUploadStatus {
  if (value === 'UPLOADED' || value === 'FAILED') {
    return value
  }
  return 'PENDING'
}

type CreatePayload = {
  type: string
  name: string
  filePathInBucket?: string
  content?: string
  initialBatchId?: string
}

export function mapSource(source: Record<string, unknown>): ProductSource {
  return {
    ...source,
    fileSizeKb: parseOptionalNumber(source.fileSizeKb),
    createdAt: new Date(source.createdAt as string),
    updatedAt: (source.updatedAt as string)
      ? new Date(source.updatedAt as string)
      : null,
    deletedAt: (source.deletedAt as string)
      ? new Date(source.deletedAt as string)
      : null,
    fileUploadStatus: parseFileUploadStatus(source.fileUploadStatus),
    ingestionStatusUpdatedAt: (source.ingestionStatusUpdatedAt as string)
      ? new Date(source.ingestionStatusUpdatedAt as string)
      : null,
    ingestionStage: (source.ingestionStage as string) ?? null,
    ingestionProgress: (source.ingestionProgress as IngestionProgress) ?? null,
    ingestionEmbeddingTokens: parseOptionalNumber(source.ingestionEmbeddingTokens),
    ingestionEmbeddingModel: parseOptionalString(source.ingestionEmbeddingModel),
    ingestionPartialResult: parseOptionalBoolean(source.ingestionPartialResult),
    ingestionMissingPages: parseStringArray(source.ingestionMissingPages),
    ingestionError: parseOptionalString(source.ingestionError),
    ingestionErrorCode: parseOptionalString(source.ingestionErrorCode),
  } as ProductSource
}

export function useListProductSources(productId: string | undefined) {
  return useQuery<ProductSource[], Error>({
    queryKey: [PRODUCT_SOURCES_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<Record<string, unknown>[]>(
        `/products/${productId}/sources`
      )
      return response.data.map((source) => mapSource(source))
    },
    enabled: Boolean(productId),
    refetchInterval(query) {
      const data = query.state.data
      if (!data) {
        return false
      }
      if (
        data.some((source) =>
          isProductSourceIngestionInFlight({
            ingestionStatus: source.ingestionStatus,
            fileUploadStatus: source.fileUploadStatus,
            filePathInBucket: source.filePathInBucket,
          })
        )
      ) {
        return 5000
      }
      if (
        data.some(
          (source) =>
            source.fileUploadStatus === 'PENDING' &&
            source.filePathInBucket.trim().length === 0
        )
      ) {
        return 5000
      }
      if (data.some((source) => shouldPollForClassification(source))) {
        return 5000
      }
      return false
    },
  })
}

export function useCreateProductSource(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<ProductSource, Error, CreatePayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<Record<string, unknown>>(
        `/products/${productId}/sources`,
        payload
      )
      return mapSource(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useProductSourceDownloadUrl(
  productId: string | undefined,
  filePathInBucket: string | undefined
) {
  return useQuery<{ url: string }, Error>({
    queryKey: [
      PRODUCT_SOURCE_DOWNLOAD_URL_KEY,
      productId,
      filePathInBucket,
    ],
    queryFn: async () => {
      const response = await apiClient.post<{ url: string }>(
        `/products/${productId}/sources/download-url`,
        { key: filePathInBucket }
      )
      return response.data
    },
    enabled: Boolean(productId) && Boolean(filePathInBucket),
    staleTime: 10 * 60 * 1000,
  })
}

export function useProductSourceChunks(
  productId: string | undefined,
  sourceId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ProductSourceChunksResponse, Error>({
    queryKey: [PRODUCT_SOURCE_CHUNKS_KEY, productId, sourceId],
    queryFn: async () => {
      const response = await apiClient.get<ProductSourceChunksResponse>(
        `/products/${productId}/sources/${sourceId}/chunks`
      )
      return response.data
    },
    enabled:
      Boolean(productId) &&
      Boolean(sourceId) &&
      (options?.enabled ?? true),
  })
}

export function useDeleteProductSource(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (sourceId) => {
      await apiClient.delete(
        `/products/${productId}/sources/${sourceId}`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useUpdateProductSourceFilePath(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    ProductSource,
    Error,
    { sourceId: string; filePathInBucket: string; fileSizeKb?: number }
  >({
    mutationFn: async ({ sourceId, filePathInBucket, fileSizeKb }) => {
      const response = await apiClient.patch<Record<string, unknown>>(
        `/products/${productId}/sources/${sourceId}/file-path`,
        {
          filePathInBucket,
          ...(fileSizeKb !== undefined ? { fileSizeKb } : {}),
        }
      )
      return mapSource(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export type UpdateProductSourceClassificationPayload = {
  sourceId: string
  classification?: string | null
  classificationPurposeOrUtility?: string | null
}

export function useUpdateProductSourceClassification(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    ProductSource,
    Error,
    UpdateProductSourceClassificationPayload
  >({
    mutationFn: async ({
      sourceId,
      classification,
      classificationPurposeOrUtility,
    }) => {
      const response = await apiClient.patch<Record<string, unknown>>(
        `/products/${productId}/sources/${sourceId}/classification`,
        {
          ...(classification !== undefined ? { classification } : {}),
          ...(classificationPurposeOrUtility !== undefined
            ? { classificationPurposeOrUtility }
            : {}),
        }
      )
      return mapSource(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
    },
  })
}

export function useUpdateProductSourceUploadStatus(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    ProductSource,
    Error,
    { sourceId: string; fileUploadStatus: FileUploadStatus }
  >({
    mutationFn: async ({ sourceId, fileUploadStatus }) => {
      const response = await apiClient.patch<Record<string, unknown>>(
        `/products/${productId}/sources/${sourceId}/upload-status`,
        { fileUploadStatus }
      )
      return mapSource(response.data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useRetryProductSourceIngestion(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ accepted: boolean }, Error, { sourceId: string }>({
    mutationFn: async ({ sourceId }) => {
      const response = await apiClient.post<{ accepted: boolean }>(
        `/products/${productId}/sources/${sourceId}/retry-ingestion`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_SOURCES_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY],
      })
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'machineryDirectiveEPlan',
      })
    },
  })
}
